import { useState, useEffect } from 'react';
import { Landmark } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { AccountFormFields } from './AccountFormFields';
import { useFormSubmission } from '@/hooks/useFormSubmission';
import { BankAccountFormData } from '@/types';
import { ModalHeader, ModalFooter, ModalBody } from '@/components/ui/modal';

interface AccountFormNestedProps {
  onClose: () => void;
  onSubmit: (data: BankAccountFormData) => Promise<void> | void;
  onSuccess?: () => void;
  initialData?: any;
}

export const AccountFormNested = ({
  onClose,
  onSubmit,
  onSuccess,
  initialData
}: AccountFormNestedProps) => {
  const { t } = useTranslation();
  const { isSubmitting, handleSubmit, resetState } = useFormSubmission();
  
  const [formData, setFormData] = useState<BankAccountFormData>({
    name: initialData?.name || '',
    bank: initialData?.bank || '',
    balance: initialData?.balance || 0,
    accountType: initialData?.accountType || initialData?.account_type || 'corrente',
    accountNumber: initialData?.accountNumber || '',
    color: initialData?.color || '#8B5CF6'
  });
  const [hasInteracted, setHasInteracted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setHasInteracted(false);
    setErrors({});
    setFormData({
      name: initialData?.name || '',
      bank: initialData?.bank || '',
      balance: initialData?.balance || 0,
      accountType: initialData?.accountType || initialData?.account_type || 'corrente',
      accountNumber: initialData?.accountNumber || '',
      color: initialData?.color || '#8B5CF6'
    });
  }, [initialData]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const form = e.currentTarget as HTMLFormElement;
    
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    e.stopPropagation();
    
    await handleSubmit(
      async () => {
        await onSubmit(formData);
      },
      () => {
        onSuccess?.();
        setFormData({
          name: '',
          bank: '',
          balance: 0,
          accountType: 'corrente',
          accountNumber: '',
          color: '#8B5CF6'
        });
        setHasInteracted(false);
        setErrors({});
      },
      onClose
    );
  };

  const handleClose = () => {
    setFormData({
      name: initialData?.name || '',
      bank: initialData?.bank || '',
      balance: initialData?.balance || 0,
      accountType: initialData?.accountType || initialData?.account_type || 'corrente',
      accountNumber: initialData?.accountNumber || '',
      color: initialData?.color || '#8B5CF6'
    });
    setHasInteracted(false);
    setErrors({});
    resetState();
    
    onClose();
  };

  const handleInputChange = (field: keyof BankAccountFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  };

  const title = initialData ? t('forms.account.title_edit') : t('forms.account.title_new');

  return (
    <div className="flex flex-col h-full">
      <ModalHeader
        title={title}
        description={t('forms.account.description')}
        icon={Landmark}
        onClose={handleClose}
      />

      <ModalBody>
        <div onKeyDown={handleKeyDown}>
          <form 
            id="nested-account-form" 
            onSubmit={handleFormSubmit} 
            className="space-y-6"
          >
            <AccountFormFields
              formData={formData}
              onChange={handleInputChange}
              errors={hasInteracted ? errors : {}}
            />
          </form>
        </div>
      </ModalBody>

      <ModalFooter
        primaryAction={{
          label: isSubmitting ? 'Salvando...' : (initialData ? t('forms.account.button_update') : t('forms.account.button_create')),
          type: 'submit',
          formId: 'nested-account-form',
          isLoading: isSubmitting
        }}
        secondaryAction={{
          label: t('forms.account.button_cancel'),
          onClick: handleClose
        }}
      />
    </div>
  );
};
